import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupItem } from '@/components/ui/input-group';
import { Loader2 } from 'lucide-react';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { OAuthDivider } from '@/components/auth/OAuthDivider';
import Logo from '@/components/ui/logo';

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_denied: 'Sign in was cancelled. Please try again.',
  oauth_invalid: 'Invalid authentication request. Please try again.',
  oauth_failed: 'Authentication failed. Please try again.',
  oauth_no_email: 'Could not retrieve email from your account.',
};

export function LoginForm({
  mode = 'signin',
  redirect,
  priceId,
  inviteId
}: {
  mode?: 'signin' | 'signup';
  redirect: string | null;
  priceId: string | null;
  inviteId: string | null;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError && OAUTH_ERROR_MESSAGES[oauthError]) {
      setError(OAUTH_ERROR_MESSAGES[oauthError]);
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPending(true);
    setError('');

    try {
      const endpoint = mode === 'signin' ? '/api/auth/signin' : '/api/auth/signup';

      // Build request body based on mode
      const requestBody = mode === 'signup'
        ? { email, password, name, confirmPassword, inviteId: inviteId || undefined }
        : { email, password };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Authentication failed');
        setPending(false);
        return;
      }

      // Full page redirect to ensure clean state (same as OAuth flow)
      window.location.href = redirect || '/dashboard/campaigns';
    } catch (err) {
      setError('An error occurred. Please try again.');
      setPending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center -mt-16 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="text-center mb-8">
            <div className="mb-2">
              <Logo />
            </div>
            <h2 className="mb-2 text-center text-3xl font-medium text-content-heading tracking-tight">
              {mode === 'signin'
                ? 'Welcome back'
                : 'Create your account'}
            </h2>
          </div>
          <div className="max-w-[345px] mx-auto">
            <OAuthButtons />
            <OAuthDivider />
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <InputGroup>
                  {mode === 'signup' && (
                    <InputGroupItem
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      maxLength={100}
                      inlineLabel="Name *"
                      labelWidth="120px"
                      placeholder="Enter your name"
                    />
                  )}
                  <InputGroupItem
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    maxLength={50}
                    inlineLabel="Email *"
                    labelWidth="120px"
                    placeholder="Enter your email"
                  />
                  <InputGroupItem
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={
                      mode === 'signin' ? 'current-password' : 'new-password'
                    }
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    maxLength={100}
                    inlineLabel="Password *"
                    labelWidth="120px"
                    placeholder="Enter your password"
                  />
                  {mode === 'signup' && (
                    <InputGroupItem
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      maxLength={100}
                      inlineLabel="Confirm *"
                      labelWidth="120px"
                      placeholder="Confirm your password"
                    />
                  )}
                </InputGroup>
              </div>
              {error && (
                <div className="text-danger-text text-sm -mt-4">{error}</div>
              )}

              {mode === 'signin' && (
                <div className="text-right -mt-4 mr-4">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}

              <div>
                <Button
                  type="submit"
                  disabled={pending}
                  className='w-full'
                  variant="primary"
                >
                  {pending ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Loading...
                    </>
                  ) : mode === 'signin' ? (
                    'Sign in'
                  ) : (
                    'Sign up'
                  )}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <span className="text-sm text-content-secondary">
                {mode === 'signin'
                  ? "Don't have an account? "
                  : 'Already have an account? '}
              </span>
              <Link
                to={`${mode === 'signin' ? '/sign-up' : '/sign-in'}${
                  redirect ? `?redirect=${redirect}` : ''
                }${priceId ? `&priceId=${priceId}` : ''}`}
                className="text-sm font-medium text-primary hover:text-primary/80"
              >
                {mode === 'signin' ? 'Sign up' : 'Sign in'}
              </Link>
            </div>
        </div>
      </div>
    </div>
  );
}

